import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { showGrowl } from '../../ui/growlState';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

type ExRow = {
  id: string;
  userMessage: string;
  assistantMessage: string;
  operatorNote: string | null;
  applicationContext: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type ShortcutRow = {
  id: string;
  userMessage: string;
  assistantMessage: string;
  sortOrder: number;
  isActive: boolean;
  fromLearning?: boolean;
  createdAt: string;
  updatedAt: string;
};

type AgentBlock = {
  id: string;
  name: string;
  model: string;
  isActive: boolean;
  exampleCount: number;
  activeExampleCount: number;
  shortcutCount?: number;
  activeShortcutCount?: number;
  examples: ExRow[];
  semanticShortcuts?: ShortcutRow[];
};

type LearningsPayload = {
  maxActivePerAgent: number;
  maxShortcutsPerAgent?: number;
  statsByLlm: Record<string, { agents: number; totalExamples: number; activeExamples: number }>;
  agents: AgentBlock[];
};

function clip(s: string, n: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function formatDraftTokens(n: number): string {
  return new Intl.NumberFormat('es-UY').format(Math.round(n));
}

function formatDraftUsd(n: number | null): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-UY', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n);
}

function tableRow(rowIndex: number): CSSProperties {
  return {
    borderTop: '1px solid #e2e8f0',
    background: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
  };
}

function IconPlus({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconBolt({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M11 3L4 14h5l-2 7 8-9h-5l1-9z"
        fill="currentColor"
      />
    </svg>
  );
}

export function LearningsPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { selectedAgentId } = useAgentSelector();
  const [data, setData] = useState<LearningsPayload | null>(null);
  const [err, setErr] = useState('');
  const [modelFilter, setModelFilter] = useState<string>('');
  const [addAgentId, setAddAgentId] = useState<string | null>(null);
  const [nUser, setNUser] = useState('');
  const [nAssist, setNAssist] = useState('');
  const [nNote, setNNote] = useState('');
  const [nBusy, setNBusy] = useState(false);
  const [editing, setEditing] = useState<{ agentId: string; ex: ExRow } | null>(null);
  const [eUser, setEUser] = useState('');
  const [eAssist, setEAssist] = useState('');
  const [eNote, setENote] = useState('');
  const [eContext, setEContext] = useState('');
  const [eActive, setEActive] = useState(true);
  const [eBusy, setEBusy] = useState(false);

  const [addShortcutAgentId, setAddShortcutAgentId] = useState<string | null>(null);
  const [shUser, setShUser] = useState('');
  const [shAssist, setShAssist] = useState('');
  const [shBusy, setShBusy] = useState(false);
  const [editingSh, setEditingSh] = useState<{ agentId: string; sc: ShortcutRow } | null>(null);
  const [eShUser, setEShUser] = useState('');
  const [eShAssist, setEShAssist] = useState('');
  const [eShActive, setEShActive] = useState(true);
  const [eShBusy, setEShBusy] = useState(false);
  const [autoDetectBusyId, setAutoDetectBusyId] = useState<string | null>(null);
  const [deleteAllGuideBusyId, setDeleteAllGuideBusyId] = useState<string | null>(null);
  const [deleteAllModalAgent, setDeleteAllModalAgent] = useState<AgentBlock | null>(null);
  const [deleteAllShortcutsBusyId, setDeleteAllShortcutsBusyId] = useState<string | null>(null);
  const [deleteAllShortcutsModalAgent, setDeleteAllShortcutsModalAgent] = useState<AgentBlock | null>(null);
  const [helpOpen, setHelpOpen] = useState(true);
  const [autoTrainFor, setAutoTrainFor] = useState<{ id: string; name: string } | null>(null);
  const [autoTrainN, setAutoTrainN] = useState(30);
  const [autoTrainItems, setAutoTrainItems] = useState<{ userMessage: string; assistantMessage: string }[]>([]);
  const [autoTrainMeta, setAutoTrainMeta] = useState<{
    model: string;
    targetName: string;
    usage: { promptTokens: number; completionTokens: number; totalTokens: number } | null;
    cost: {
      estimatedUsd: number | null;
      inputUsdPerMillion: number | null;
      outputUsdPerMillion: number | null;
    } | null;
  } | null>(null);
  const [autoTrainBusy, setAutoTrainBusy] = useState(false);
  const [autoTrainPhase, setAutoTrainPhase] = useState<'idle' | 'propose' | 'commit'>('idle');
  /** 0–100: borrador = pares generados / objetivo; commit = estimado interno. */
  const [autoTrainProgress, setAutoTrainProgress] = useState(0);
  /** Pares ya generados (SSE) mientras dura el borrador LLM. */
  const [autoTrainPairDone, setAutoTrainPairDone] = useState(0);
  /** '' = usar el `model` del agente; si no, `modelId` de preset permitido. */
  const [draftLlmModelId, setDraftLlmModelId] = useState('');
  const [aiModelPresets, setAiModelPresets] = useState<{ id: string; label: string }[]>([]);
  /** Por agente: secciones colapsables (inicio cerradas). */
  const [guideSectionOpen, setGuideSectionOpen] = useState<Record<string, boolean>>({});
  const [autoGenSectionOpen, setAutoGenSectionOpen] = useState<Record<string, boolean>>({});
  const [shortcutsSectionOpen, setShortcutsSectionOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await apiGet<LearningsPayload>('/api/console/learnings', token);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const rows = await apiGet<{ id: string; label: string }[]>('/api/console/ai-model-presets', token);
        setAiModelPresets(rows);
      } catch {
        setAiModelPresets([]);
      }
    })();
  }, [token]);

  const draftTargetAgent = useMemo(
    () => (data && autoTrainFor ? data.agents.find((a) => a.id === autoTrainFor.id) : undefined),
    [data, autoTrainFor]
  );

  useEffect(() => {
    if (!editing && !editingSh) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditing(null);
        setEditingSh(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, editingSh]);

  useEffect(() => {
    if (!autoTrainFor) {
      setAutoTrainPhase('idle');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !autoTrainBusy) setAutoTrainFor(null);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [autoTrainFor, autoTrainBusy]);

  useEffect(() => {
    if (!deleteAllModalAgent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteAllGuideBusyId) setDeleteAllModalAgent(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteAllModalAgent, deleteAllGuideBusyId]);

  useEffect(() => {
    if (!deleteAllShortcutsModalAgent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteAllShortcutsBusyId) setDeleteAllShortcutsModalAgent(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteAllShortcutsModalAgent, deleteAllShortcutsBusyId]);

  const modelOptions = useMemo(() => {
    if (!data) return [] as string[];
    return Object.keys(data.statsByLlm).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const visibleAgents = useMemo(() => {
    if (!data) return [];
    if (!modelFilter && !selectedAgentId) return data.agents;
    return data.agents.filter((a) => {
      if (selectedAgentId && a.id !== selectedAgentId) return false;
      if (modelFilter && (a.model?.trim() || '—') !== modelFilter) return false;
      return true;
    });
  }, [data, modelFilter, selectedAgentId]);

  const startAdd = (agentId: string) => {
    setAddAgentId((x) => {
      const next = x === agentId ? null : agentId;
      if (next) setGuideSectionOpen((s) => ({ ...s, [next]: true }));
      return next;
    });
    setNUser('');
    setNAssist('');
    setNNote('');
  };

  const submitAdd = async (e: React.FormEvent, agentId: string) => {
    e.preventDefault();
    if (!nUser.trim() || !nAssist.trim()) {
      setErr('Completá pregunta del visitante y respuesta deseada.');
      return;
    }
    setNBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/agents/${agentId}/response-examples`,
        {
          userMessage: nUser.trim(),
          assistantMessage: nAssist.trim(),
          operatorNote: nNote.trim() || undefined,
        },
        token,
        { successMessage: 'Aprendizaje añadido' }
      );
      setAddAgentId(null);
      setNUser('');
      setNAssist('');
      setNNote('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setNBusy(false);
    }
  };

  const openEdit = (agentId: string, ex: ExRow) => {
    setGuideSectionOpen((s) => ({ ...s, [agentId]: true }));
    setEditing({ agentId, ex });
    setEUser(ex.userMessage);
    setEAssist(ex.assistantMessage);
    setENote(ex.operatorNote ?? '');
    setEContext(ex.applicationContext ?? '');
    setEActive(ex.isActive);
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setEBusy(true);
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${editing.agentId}/response-examples/${editing.ex.id}`,
        { userMessage: eUser.trim(), assistantMessage: eAssist.trim(), operatorNote: eNote.trim() || null, applicationContext: eContext.trim() || null, isActive: eActive },
        token,
        { successMessage: 'Aprendizaje actualizado' }
      );
      setEditing(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setEBusy(false);
    }
  };

  const remove = async (agentId: string, exId: string) => {
    if (!confirm('¿Eliminar este aprendizaje?')) return;
    setErr('');
    try {
      await apiDelete(`/api/console/agents/${agentId}/response-examples/${exId}`, token, {
        successMessage: 'Eliminado',
      });
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const startAddShortcut = (agentId: string) => {
    setAddShortcutAgentId((x) => {
      const next = x === agentId ? null : agentId;
      if (next) setShortcutsSectionOpen((s) => ({ ...s, [next]: true }));
      return next;
    });
    setShUser('');
    setShAssist('');
  };

  const submitAddShortcut = async (e: React.FormEvent, agentId: string) => {
    e.preventDefault();
    if (!shUser.trim() || !shAssist.trim()) {
      setErr('Completá pregunta y respuesta del atajo.');
      return;
    }
    setShBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/agents/${agentId}/semantic-shortcuts`,
        { userMessage: shUser.trim(), assistantMessage: shAssist.trim() },
        token,
        { successMessage: 'Atajo añadido' }
      );
      setAddShortcutAgentId(null);
      setShUser('');
      setShAssist('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setShBusy(false);
    }
  };

  const openEditShortcut = (agentId: string, sc: ShortcutRow) => {
    setShortcutsSectionOpen((s) => ({ ...s, [agentId]: true }));
    setEditingSh({ agentId, sc });
    setEShUser(sc.userMessage);
    setEShAssist(sc.assistantMessage);
    setEShActive(sc.isActive);
  };

  const saveEditShortcut = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!editingSh) return;
    setEShBusy(true);
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${editingSh.agentId}/semantic-shortcuts/${editingSh.sc.id}`,
        { userMessage: eShUser.trim(), assistantMessage: eShAssist.trim(), isActive: eShActive },
        token,
        { successMessage: 'Atajo actualizado' }
      );
      setEditingSh(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setEShBusy(false);
    }
  };

  const removeShortcut = async (agentId: string, scId: string) => {
    if (!confirm('¿Eliminar este atajo semántico?')) return;
    setErr('');
    try {
      await apiDelete(`/api/console/agents/${agentId}/semantic-shortcuts/${scId}`, token, {
        successMessage: 'Atajo eliminado',
      });
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  type AutoDetectData = {
    dryRun: boolean;
    options: { minOccurrences: number; minAgreementRatio: number; lookbackDays: number; maxMessages: number; dryRun: boolean };
    candidates: Array<{ userMessage: string; assistantMessage: string; occurrences: number; agreement: number }>;
    created: Array<{ id: string; userMessage: string; assistantMessage: string }>;
    skipped: Array<{ key: string; reason: string }>;
  };

  const runAutoTrainPropose = async () => {
    if (!autoTrainFor) return;
    if (autoTrainN < 1 || autoTrainN > 200) {
      setErr('Cantidad: entre 1 y 200 (máx. alineado con atajos activos por agente).');
      return;
    }
    setAutoTrainBusy(true);
    setAutoTrainPhase('propose');
    setAutoTrainItems([]);
    setAutoTrainMeta(null);
    setErr('');
    setAutoTrainProgress(0);
    setAutoTrainPairDone(0);
    const applyProgress = (generated: number, target: number) => {
      const t = target > 0 ? target : autoTrainN;
      setAutoTrainPairDone(generated);
      setAutoTrainProgress(t > 0 ? Math.min(100, (generated / t) * 100) : 0);
    };
    try {
      const r = await fetch(`/api/console/agents/${autoTrainFor.id}/auto-training/propose-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          questionCount: autoTrainN,
          ...(draftLlmModelId.trim() ? { modelId: draftLlmModelId.trim() } : {}),
        }),
        signal: AbortSignal.timeout(
          Math.max(120_000, Math.min(1_200_000, 60_000 + autoTrainN * 5_000))
        ),
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const j = (await r.json()) as { error?: { message?: string } };
          if (j?.error?.message) msg = j.error.message;
        } catch {
          /* ign */
        }
        throw new Error(msg);
      }
      const reader = r.body?.getReader();
      if (!reader) {
        throw new Error('El navegador no soporta lectura del stream (body vacío).');
      }
      const dec = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        for (;;) {
          const sep = buffer.indexOf('\n\n');
          if (sep === -1) break;
          const raw = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const dataLine = raw
            .split('\n')
            .find((l) => l.startsWith('data:'))
            ?.slice(5)
            .trim();
          if (!dataLine) continue;
          type Ev =
            | { type: 'progress'; generated: number; target: number }
            | {
                type: 'complete';
                data: {
                  targetAgent: { id: string; name: string };
                  items: { userMessage: string; assistantMessage: string }[];
                  model: string;
                  usage: { promptTokens: number; completionTokens: number; totalTokens: number } | null;
                  cost: {
                    estimatedUsd: number | null;
                    inputUsdPerMillion: number | null;
                    outputUsdPerMillion: number | null;
                  } | null;
                };
              }
            | { type: 'error'; code?: string; message: string };
          let ev: Ev;
          try {
            ev = JSON.parse(dataLine) as Ev;
          } catch {
            continue;
          }
          if (ev.type === 'progress') {
            applyProgress(ev.generated, ev.target);
          } else if (ev.type === 'error') {
            throw new Error(ev.message);
          } else if (ev.type === 'complete') {
            setAutoTrainItems(ev.data.items);
            setAutoTrainMeta({
              targetName: ev.data.targetAgent.name,
              model: ev.data.model,
              usage: ev.data.usage ?? null,
              cost: ev.data.cost ?? null,
            });
            setAutoTrainProgress(100);
            setAutoTrainPairDone(ev.data.items.length);
            showGrowl('Borrador generado; revisá y editá antes de guardar en atajos.', 'success');
            await new Promise((res) => setTimeout(res, 400));
          }
        }
      }
    } catch (ex) {
      setAutoTrainProgress(0);
      setAutoTrainPairDone(0);
      setErr((ex as Error).message);
    } finally {
      setAutoTrainBusy(false);
      setAutoTrainPhase('idle');
      setAutoTrainProgress(0);
      setAutoTrainPairDone(0);
    }
  };

  const runAutoTrainCommit = async () => {
    if (!autoTrainFor || autoTrainItems.length === 0) return;
    setAutoTrainBusy(true);
    setAutoTrainPhase('commit');
    setErr('');
    setAutoTrainProgress(0);
    const cap = 95;
    const commitTimer = window.setInterval(() => {
      setAutoTrainProgress((p) => {
        if (p >= cap) return cap;
        const room = cap - p;
        return Math.min(cap, p + Math.max(0.8, room * 0.12 + 0.4));
      });
    }, 100);
    try {
      await apiPost<unknown>(
        `/api/console/agents/${autoTrainFor.id}/auto-training/commit`,
        { items: autoTrainItems },
        token,
        { timeoutMs: 120_000, successMessage: 'Atajos añadidos' }
      );
      window.clearInterval(commitTimer);
      setAutoTrainProgress(100);
      await new Promise((r) => setTimeout(r, 420));
      setAutoTrainFor(null);
      setAutoTrainItems([]);
      setAutoTrainMeta(null);
      setShortcutsSectionOpen((s) => ({ ...s, [autoTrainFor.id]: true }));
      void load();
    } catch (ex) {
      window.clearInterval(commitTimer);
      setAutoTrainProgress(0);
      setErr((ex as Error).message);
    } finally {
      window.clearInterval(commitTimer);
      setAutoTrainBusy(false);
      setAutoTrainPhase('idle');
      setAutoTrainProgress(0);
    }
  };

  const openDeleteAllModal = (a: AgentBlock) => {
    if (a.exampleCount === 0) return;
    setDeleteAllModalAgent(a);
  };

  const confirmDeleteAllGuide = async () => {
    const a = deleteAllModalAgent;
    if (!a || a.exampleCount === 0) return;
    setDeleteAllGuideBusyId(a.id);
    setErr('');
    try {
      const out = await apiDelete<{ deleted: number }>(`/api/console/agents/${a.id}/response-examples`, token);
      showGrowl(
        `Se eliminaron ${out.deleted} par${out.deleted === 1 ? '' : 'es'} de aprendizaje de la guía.`,
        'success'
      );
      setGuideSectionOpen((s) => ({ ...s, [a.id]: true }));
      setDeleteAllModalAgent(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setDeleteAllGuideBusyId(null);
    }
  };

  const shortcutCountFor = (a: AgentBlock) =>
    a.shortcutCount ?? (a.semanticShortcuts?.length ?? 0);

  const openDeleteAllShortcutsModal = (a: AgentBlock) => {
    if (shortcutCountFor(a) === 0) return;
    setDeleteAllShortcutsModalAgent(a);
  };

  const confirmDeleteAllShortcuts = async () => {
    const a = deleteAllShortcutsModalAgent;
    if (!a || shortcutCountFor(a) === 0) return;
    setDeleteAllShortcutsBusyId(a.id);
    setErr('');
    try {
      const out = await apiDelete<{ deleted: number }>(`/api/console/agents/${a.id}/semantic-shortcuts`, token);
      showGrowl(
        `Se eliminaron ${out.deleted} atajo${out.deleted === 1 ? '' : 's'} semántico${out.deleted === 1 ? '' : 's'}.`,
        'success'
      );
      setShortcutsSectionOpen((s) => ({ ...s, [a.id]: true }));
      setDeleteAllShortcutsModalAgent(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setDeleteAllShortcutsBusyId(null);
    }
  };

  const runAutoDetect = async (agentId: string, dryRun: boolean) => {
    setAutoGenSectionOpen((s) => ({ ...s, [agentId]: true }));
    setAutoDetectBusyId(agentId);
    setErr('');
    try {
      const d = await apiPost<AutoDetectData>(
        `/api/console/agents/${agentId}/semantic-shortcuts/auto-detect`,
        { dryRun, minOccurrences: 3, minAgreementRatio: 0.6, lookbackDays: 90 },
        token
      );
      if (dryRun) {
        const pct = (d.options.minAgreementRatio * 100).toFixed(0);
        showGrowl(
          `Vista previa: ${d.candidates.length} candidato(s) (mín. ${d.options.minOccurrences} repeticiones, acuerdo ≥${pct}%, últimos ${d.options.lookbackDays} d.; omitidos: ${d.skipped.length}).`,
          'info'
        );
      } else if (d.created.length > 0) {
        showGrowl(`Se crearon ${d.created.length} atajo(s) automático(s) a partir de conversaciones reales.`, 'success');
        setShortcutsSectionOpen((s) => ({ ...s, [agentId]: true }));
        void load();
      } else {
        showGrowl(
          'Ningún atajo nuevo: hacé una vista previa o no hay mensajes que cumplan criterio (frecuencia y misma respuesta).',
          'info'
        );
      }
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setAutoDetectBusyId(null);
    }
  };

  return (
    <>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 1080,
          margin: '0 auto',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '28px 24px 40px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 20,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#0f172a',
                }}
              >
                Aprendizajes
              </h1>
              <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
                Ejemplos y atajos semánticos que entrenan a tus agentes sin llamar al modelo.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => void load()}
                style={btnGhost}
                title="Recargar datos"
              >
                ⟳ Actualizar
              </button>
              <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                style={btnGhost}
                aria-expanded={helpOpen}
              >
                {helpOpen ? 'Ocultar ayuda' : 'Cómo funciona'}
              </button>
            </div>
          </div>

          {helpOpen ? (
            <div
              style={{
                marginBottom: 22,
                padding: '16px 18px',
                borderRadius: 12,
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '1px solid #e2e8f0',
                borderLeft: '4px solid #7c3aed',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.65, maxWidth: 900 }}>
                <strong>Guía:</strong> cada par visitante → asistente se inyecta en el prompt (few-shot; no reentrena el
                modelo). <strong>Generación automática:</strong> el borrador LLM propone atajos según prompt, documentos y
                guía; la detección por conversaciones busca preguntas repetidas. <strong>Atajos:</strong> respuesta fija
                por texto idéntico o <strong>coseno</strong> (ver <code>SEMANTIC_SHORTCUT_MIN_COSINE</code> y embeddings).
                Corregir en chat (👎) suma guía; marcar útil (👍) suma atajo manual.
              </p>
            </div>
          ) : null}

          {data ? (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
                marginBottom: 24,
                padding: '12px 16px',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#334155',
                }}
              >
                <span>Modelo LLM</span>
                <select
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  style={selectFilter}
                >
                  <option value="">Todos los modelos</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m} — {data.statsByLlm[m]!.activeExamples} activos / {data.statsByLlm[m]!.totalExamples} total ·{' '}
                      {data.statsByLlm[m]!.agents} agente(s)
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

      {err ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          {err}
        </div>
      ) : null}

      {!data && !err ? (
        <div
          style={{
            padding: '40px 24px',
            textAlign: 'center',
            borderRadius: 16,
            background: '#f8fafc',
            border: '1px dashed #e2e8f0',
          }}
        >
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#475569' }}>Cargando aprendizajes…</p>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#94a3b8' }}>Esto debería tardar solo un instante.</p>
        </div>
      ) : !data ? null : data.agents.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 24px',
            borderRadius: 16,
            background: '#f8fafc',
            border: '1px dashed #cbd5e1',
          }}
        >
          <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>
            No hay agentes. Creá uno en{' '}
            <Link to="/app/agents" style={{ color: '#7c3aed', fontWeight: 600 }}>
              Agentes virtuales
            </Link>
            .
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {visibleAgents.length === 0 && modelFilter ? (
            <p style={{ color: '#64748b', fontSize: 14 }}>Ningún agente con ese modelo. Cambiá el filtro o probá “Todos”.</p>
          ) : null}
          {visibleAgents.map((a) => {
            const guOpen = guideSectionOpen[a.id] ?? false;
            const autoGenOpen = autoGenSectionOpen[a.id] ?? false;
            const shOpen = shortcutsSectionOpen[a.id] ?? false;
            return (
            <section
              key={a.id}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 16,
                padding: 0,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.04)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '18px 20px',
                  background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{a.name}</h2>
                  <div
                    style={{
                      marginTop: 10,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <code style={codePill}>{a.model}</code>
                    <span style={a.isActive ? pillOn : pillOff}>{a.isActive ? 'En producción' : 'Inactivo'}</span>
                    <span style={pillStat}>
                      Guía {a.activeExampleCount}/{data.maxActivePerAgent} activos
                      {a.exampleCount > a.activeExampleCount ? ` · +${a.exampleCount - a.activeExampleCount} inactivos` : ''}
                    </span>
                    <span style={pillStat}>
                      Atajos {a.activeShortcutCount ?? 0}/{data.maxShortcutsPerAgent ?? 100} activos
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <Link to="/app/agents" style={btnLink}>
                    Editar agente
                  </Link>
                </div>
              </div>
              <div style={{ padding: '0 20px 20px' }}>

              <div style={{ marginTop: 18 }}>
                <div style={sectionCollapseHeader}>
                  <button
                    type="button"
                    onClick={() => setGuideSectionOpen((p) => ({ ...p, [a.id]: !guOpen }))}
                    style={sectionCollapseToggle}
                    aria-expanded={guOpen}
                    aria-controls={`guide-panel-${a.id}`}
                    id={`guide-trigger-${a.id}`}
                  >
                    <span style={sectionChevron} aria-hidden>
                      {guOpen ? '▼' : '▸'}
                    </span>
                    <span style={sectionTitleInToggle}>Guía (few-shot)</span>
                    <span style={sectionCountPill}>
                      {a.exampleCount} {a.exampleCount === 1 ? 'par' : 'pares'}
                    </span>
                  </button>
                  <div
                    style={sectionHeaderIconGroup}
                    role="group"
                    aria-label="Acciones de la guía"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => startAdd(a.id)}
                      style={addAgentId === a.id ? headerIconBtnActive : headerIconBtn}
                      title={addAgentId === a.id ? 'Cerrar formulario' : 'Añadir a la guía'}
                      aria-label={addAgentId === a.id ? 'Cerrar formulario' : 'Añadir a la guía'}
                      aria-pressed={addAgentId === a.id}
                    >
                      <IconPlus />
                    </button>
                    <button
                      type="button"
                      onClick={() => openDeleteAllModal(a)}
                      style={
                        a.exampleCount === 0
                          ? { ...headerIconBtnDanger, opacity: 0.4, cursor: 'not-allowed' as const }
                          : deleteAllGuideBusyId === a.id
                            ? { ...headerIconBtnDanger, opacity: 0.85, cursor: 'wait' as const }
                            : headerIconBtnDanger
                      }
                      disabled={a.exampleCount === 0 || deleteAllGuideBusyId === a.id}
                      title="Borrar toda la guía de este agente"
                      aria-label="Borrar toda la guía"
                    >
                      {deleteAllGuideBusyId === a.id ? '…' : <IconTrash />}
                    </button>
                  </div>
                </div>

                {addAgentId === a.id ? (
                  <form
                    onSubmit={(ev) => void submitAdd(ev, a.id)}
                    style={{ marginTop: 8, marginBottom: 8, padding: 16, background: '#f5f3ff', borderRadius: 12, border: '1px solid #c4b5fd' }}
                  >
                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 10px' }}>
                      Definí de antemano un par <strong>visitante → asistente</strong> (misma lógica que en un chat con
                      “evaluación” negativa).
                    </p>
                    <label style={lab}>
                      Pregunta / mensaje del visitante *
                      <textarea
                        value={nUser}
                        onChange={(e) => setNUser(e.target.value)}
                        required
                        style={ta}
                        rows={2}
                        disabled={nBusy}
                      />
                    </label>
                    <label style={lab}>
                      Respuesta deseada del asistente *
                      <textarea
                        value={nAssist}
                        onChange={(e) => setNAssist(e.target.value)}
                        required
                        style={ta}
                        rows={3}
                        disabled={nBusy}
                      />
                    </label>
                    <label style={lab}>
                      Nota del operador (opcional, va a la guía)
                      <textarea value={nNote} onChange={(e) => setNNote(e.target.value)} style={ta} rows={2} disabled={nBusy} />
                    </label>
                    <button type="submit" style={btnPri} disabled={nBusy}>
                      {nBusy ? 'Guardando…' : 'Guardar par'}
                    </button>
                  </form>
                ) : null}

                {guOpen ? (
                  <div id={`guide-panel-${a.id}`} role="region" aria-labelledby={`guide-trigger-${a.id}`}>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 12px', lineHeight: 1.55, paddingLeft: 4 }}>
                      El modelo usa estos pares en el prompt cuando el tema encaja. No crea atajos automáticamente.
                    </p>
                    {a.examples.length === 0 ? (
                      <div style={emptyWell}>
                        <span style={{ fontSize: 22, opacity: 0.35, lineHeight: 1 }}>◇</span>
                        <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Todavía no hay pares en la guía de este agente.</p>
                        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#94a3b8' }}>
                          Usá el botón + en el encabezado o corregí un mensaje desde una conversación (👎).
                        </p>
                      </div>
                    ) : (
                      <div style={tableShellScrollable} title="Hacé scroll para ver más filas (≈8 visibles)">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th style={thHead}>Activo</th>
                              <th style={thHead}>Visitante</th>
                              <th style={thHead}>Asistente</th>
                              <th style={thHead}>Contexto</th>
                              <th style={thHead}>Nota</th>
                              <th style={{ ...thHead, width: 1, whiteSpace: 'nowrap' as const }} />
                            </tr>
                          </thead>
                          <tbody>
                            {a.examples.map((r, rowIdx) => (
                              <tr key={r.id} style={tableRow(rowIdx)}>
                                <td style={td}>
                                  {r.isActive ? <span style={badgeYes}>Sí</span> : <span style={badgeNo}>No</span>}
                                </td>
                                <td style={td} title={r.userMessage}>
                                  {clip(r.userMessage, 64)}
                                </td>
                                <td style={td} title={r.assistantMessage}>
                                  {clip(r.assistantMessage, 80)}
                                </td>
                                <td style={td} title={r.applicationContext ?? ''}>
                                  {r.applicationContext ? clip(r.applicationContext, 48) : '—'}
                                </td>
                                <td style={td} title={r.operatorNote ?? ''}>
                                  {r.operatorNote ? clip(r.operatorNote, 40) : '—'}
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                  <button type="button" style={btnSm} onClick={() => openEdit(a.id, r)}>
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    style={{ ...btnSm, color: '#b91c1c' }}
                                    onClick={() => void remove(a.id, r.id)}
                                  >
                                    Borrar
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={sectionCollapseHeader}>
                  <button
                    type="button"
                    onClick={() => setAutoGenSectionOpen((p) => ({ ...p, [a.id]: !autoGenOpen }))}
                    style={sectionCollapseToggle}
                    aria-expanded={autoGenOpen}
                    aria-controls={`autogen-panel-${a.id}`}
                    id={`autogen-trigger-${a.id}`}
                  >
                    <span style={sectionChevron} aria-hidden>
                      {autoGenOpen ? '▼' : '▸'}
                    </span>
                    <span style={sectionTitleInToggle}>Generación automática de atajos</span>
                  </button>
                </div>
                {autoGenOpen ? (
                  <div id={`autogen-panel-${a.id}`} role="region" aria-labelledby={`autogen-trigger-${a.id}`}>
                    <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 16px', lineHeight: 1.55, paddingLeft: 4 }}>
                      Herramientas para crear atajos semánticos sin escribirlos uno a uno. El resultado siempre se guarda
                      en <strong>atajos</strong> (no en la guía few-shot).
                    </p>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border: '1px solid #ddd6fe',
                        background: 'linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)',
                        marginBottom: 14,
                      }}
                    >
                      <h3 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#5b21b6' }}>
                        Desde contexto del agente (LLM)
                      </h3>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                        El modelo recibe el mismo armado que <strong>Simular visitante</strong> (prompt, guía activa,
                        documentos y reglas) y propone pares visitante → respuesta. Revisás el borrador antes de guardar.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          if (autoTrainFor?.id === a.id) {
                            if (!autoTrainBusy) setAutoTrainFor(null);
                            return;
                          }
                          setErr('');
                          setAutoTrainPhase('idle');
                          setAutoTrainFor({ id: a.id, name: a.name });
                          setDraftLlmModelId('');
                          setAutoTrainItems([]);
                          setAutoTrainMeta(null);
                        }}
                        style={autoTrainFor?.id === a.id ? btnPrimarySoft : btnPri}
                        title="Abre el asistente de borrador con LLM"
                      >
                        {autoTrainFor?.id === a.id ? 'Cerrar borrador (LLM)' : 'Generar borrador desde contexto'}
                      </button>
                    </div>

                    <div
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border: '1px solid #bae6fd',
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfeff 100%)',
                      }}
                    >
                      <h3 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#0369a1' }}>
                        Desde conversaciones reales
                      </h3>
                      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                        Detecta preguntas del visitante que se repiten con la misma respuesta del agente en el historial
                        (sin llamar al LLM).
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          style={btnSubtle}
                          disabled={autoDetectBusyId === a.id}
                          title="Lista candidatos según el historial (no guarda nada)"
                          onClick={() => void runAutoDetect(a.id, true)}
                        >
                          {autoDetectBusyId === a.id ? 'Analizando…' : 'Vista previa frecuentes'}
                        </button>
                        <button
                          type="button"
                          style={btnSubtle}
                          disabled={autoDetectBusyId === a.id}
                          title="Crea atajos a partir de pares visitante→agente repetidos"
                          onClick={() => {
                            if (
                              !confirm(
                                '¿Crear atajos automáticos a partir de conversaciones? No duplica guía activa ni atajos existentes.'
                              )
                            ) {
                              return;
                            }
                            void runAutoDetect(a.id, false);
                          }}
                        >
                          Crear desde conversaciones
                        </button>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                          Criterio: mín. 3 repeticiones, misma respuesta en ≥60% (90 d.).
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                <div style={sectionCollapseHeader}>
                  <button
                    type="button"
                    onClick={() => setShortcutsSectionOpen((p) => ({ ...p, [a.id]: !shOpen }))}
                    style={sectionCollapseToggle}
                    aria-expanded={shOpen}
                    aria-controls={`shortcuts-panel-${a.id}`}
                    id={`shortcuts-trigger-${a.id}`}
                  >
                    <span style={sectionChevron} aria-hidden>
                      {shOpen ? '▼' : '▸'}
                    </span>
                    <span style={sectionTitleInToggle}>Atajos semánticos</span>
                    <span style={sectionCountPill}>
                      {(a.semanticShortcuts ?? []).length}{' '}
                      {(a.semanticShortcuts ?? []).length === 1 ? 'atajo' : 'atajos'}
                    </span>
                  </button>
                  <div
                    style={sectionHeaderIconGroup}
                    role="group"
                    aria-label="Acciones de atajos"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => startAddShortcut(a.id)}
                      style={addShortcutAgentId === a.id ? headerIconBtnShortcutOn : headerIconBtnShortcut}
                      title={addShortcutAgentId === a.id ? 'Cerrar formulario' : 'Añadir atajo semántico'}
                      aria-label={addShortcutAgentId === a.id ? 'Cerrar formulario' : 'Añadir atajo semántico'}
                      aria-pressed={addShortcutAgentId === a.id}
                    >
                      <IconBolt />
                    </button>
                    <button
                      type="button"
                      onClick={() => openDeleteAllShortcutsModal(a)}
                      style={
                        shortcutCountFor(a) === 0
                          ? { ...headerIconBtnDanger, opacity: 0.4, cursor: 'not-allowed' as const }
                          : deleteAllShortcutsBusyId === a.id
                            ? { ...headerIconBtnDanger, opacity: 0.85, cursor: 'wait' as const }
                            : headerIconBtnDanger
                      }
                      disabled={shortcutCountFor(a) === 0 || deleteAllShortcutsBusyId === a.id}
                      title="Borrar todos los atajos semánticos de este agente"
                      aria-label="Borrar todos los atajos semánticos"
                    >
                      {deleteAllShortcutsBusyId === a.id ? '…' : <IconTrash />}
                    </button>
                  </div>
                </div>
                {shOpen ? (
                  <div id={`shortcuts-panel-${a.id}`} role="region" aria-labelledby={`shortcuts-trigger-${a.id}`}>
                    {addShortcutAgentId === a.id ? (
                      <form
                        onSubmit={(ev) => void submitAddShortcut(ev, a.id)}
                        style={{ marginTop: 12, marginBottom: 8, padding: 16, background: '#f0fdf4', borderRadius: 12, border: '1px solid #86efac' }}
                      >
                        <p style={{ fontSize: 12, color: '#166534', margin: '0 0 10px' }}>
                          <strong>Atajo semántico</strong>: si el visitante escribe esto (o algo muy similar), se responde sin LLM.
                        </p>
                        <label style={lab}>
                          Pregunta / mensaje del visitante *
                          <textarea
                            value={shUser}
                            onChange={(e) => setShUser(e.target.value)}
                            required
                            style={ta}
                            rows={2}
                            disabled={shBusy}
                          />
                        </label>
                        <label style={lab}>
                          Respuesta fija (se muestra con * en consola) *
                          <textarea
                            value={shAssist}
                            onChange={(e) => setShAssist(e.target.value)}
                            required
                            style={ta}
                            rows={3}
                            disabled={shBusy}
                          />
                        </label>
                        <button type="submit" style={btnPri} disabled={shBusy}>
                          {shBusy ? 'Guardando…' : 'Guardar atajo'}
                        </button>
                      </form>
                    ) : null}
                    <p style={{ fontSize: 13, color: '#64748b', margin: '8px 0 12px', lineHeight: 1.5, paddingLeft: 4 }}>
                      Atajos activos: manuales, 👍 en conversaciones o los creados en generación automática. Sin LLM si hay
                      match de texto o similitud (embeddings). Independiente de la guía.
                    </p>
                    {(a.semanticShortcuts ?? []).length === 0 ? (
                      <div style={emptyWell}>
                        <span style={{ fontSize: 22, opacity: 0.35 }}>⚡</span>
                        <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Sin atajos todavía.</p>
                        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#94a3b8' }}>
                          Añadí uno con el botón de rayo, usá generación automática o marcá 👍 en conversaciones.
                        </p>
                      </div>
                    ) : (
                      <div style={tableShellScrollable} title="Hacé scroll para ver más filas (≈8 visibles)">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th style={thHead}>Activo</th>
                              <th style={thHead}>Origen</th>
                              <th style={thHead}>Visitante</th>
                              <th style={thHead}>Respuesta fija</th>
                              <th style={{ ...thHead, width: 1, whiteSpace: 'nowrap' as const }} />
                            </tr>
                          </thead>
                          <tbody>
                            {(a.semanticShortcuts ?? []).map((r, rowIdx) => (
                              <tr key={r.id} style={tableRow(rowIdx)}>
                                <td style={td}>
                                  {r.isActive ? <span style={badgeYes}>Sí</span> : <span style={badgeNo}>No</span>}
                                </td>
                                <td
                                  style={td}
                                  title={
                                    r.fromLearning
                                      ? 'Atajo legado vinculado a un par de guía (ya no se crean nuevos así)'
                                      : 'Creado manualmente, con 👍 en chat o detección automática'
                                  }
                                >
                                  {r.fromLearning ? (
                                    <span style={{ color: '#0369a1', fontWeight: 600 }}>Legado</span>
                                  ) : (
                                    <span style={{ color: '#64748b' }}>Manual</span>
                                  )}
                                </td>
                                <td style={td} title={r.userMessage}>
                                  {clip(r.userMessage, 64)}
                                </td>
                                <td style={td} title={r.assistantMessage}>
                                  {clip(r.assistantMessage, 80)}
                                </td>
                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                  <button type="button" style={btnSm} onClick={() => openEditShortcut(a.id, r)}>
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    style={{ ...btnSm, color: '#b91c1c' }}
                                    onClick={() => void removeShortcut(a.id, r.id)}
                                  >
                                    Borrar
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              </div>
            </section>
            );
          })}
        </div>
      )}
        </div>
      </div>

      {editingSh ? (
        <div
          style={modalOverlay}
          role="dialog"
          aria-modal
          aria-labelledby="edit-sh-title"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) setEditingSh(null);
          }}
        >
          <form
            onSubmit={(ev) => void saveEditShortcut(ev)}
            style={modalPanel}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="edit-sh-title" style={modalTitle}>
              Editar atajo semántico
            </h3>
            <label style={lab}>
              Visitante
              <textarea value={eShUser} onChange={(e) => setEShUser(e.target.value)} required style={ta} rows={2} disabled={eShBusy} />
            </label>
            <label style={lab}>
              Respuesta fija
              <textarea value={eShAssist} onChange={(e) => setEShAssist(e.target.value)} required style={ta} rows={3} disabled={eShBusy} />
            </label>
            <label style={{ ...lab, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={eShActive} onChange={(e) => setEShActive(e.target.checked)} disabled={eShBusy} />
              Activo (máx. {data?.maxShortcutsPerAgent ?? 100} activos por agente)
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button type="submit" style={btnPri} disabled={eShBusy}>
                {eShBusy ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" style={btnOutline} onClick={() => setEditingSh(null)} disabled={eShBusy}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {deleteAllModalAgent ? (
        <div
          style={modalOverlay}
          role="alertdialog"
          aria-modal
          aria-labelledby="delete-all-guide-title"
          aria-describedby="delete-all-guide-desc"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget && !deleteAllGuideBusyId) setDeleteAllModalAgent(null);
          }}
        >
          <div style={modalPanel} onMouseDown={(e) => e.stopPropagation()}>
            <h3 id="delete-all-guide-title" style={modalTitle}>
              Borrar toda la guía
            </h3>
            <p
              id="delete-all-guide-desc"
              style={{ margin: '0 0 14px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}
            >
              ¿Eliminar toda la guía (aprendizaje few-shot) del agente <strong>«{deleteAllModalAgent.name}»</strong> — modelo:{' '}
              <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
                {deleteAllModalAgent.model?.trim() || '—'}
              </code>
              ?
            </p>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
              Se borrarán <strong>{deleteAllModalAgent.exampleCount}</strong> par
              {deleteAllModalAgent.exampleCount === 1 ? '' : 'es'}. Los atajos generados a partir de esas filas se
              eliminarán; el resto de atajos se mantienen.
            </p>
            <p style={{ margin: '0 0 20px', fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>Esta acción no se puede deshacer.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={btnOutline}
                disabled={!!deleteAllGuideBusyId}
                onClick={() => {
                  if (!deleteAllGuideBusyId) setDeleteAllModalAgent(null);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={btnModalDanger}
                disabled={!!deleteAllGuideBusyId}
                onClick={() => void confirmDeleteAllGuide()}
              >
                {deleteAllGuideBusyId ? 'Borrando…' : 'Sí, borrar toda la guía'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteAllShortcutsModalAgent ? (
        <div
          style={modalOverlay}
          role="alertdialog"
          aria-modal
          aria-labelledby="delete-all-shortcuts-title"
          aria-describedby="delete-all-shortcuts-desc"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget && !deleteAllShortcutsBusyId) setDeleteAllShortcutsModalAgent(null);
          }}
        >
          <div style={modalPanel} onMouseDown={(e) => e.stopPropagation()}>
            <h3 id="delete-all-shortcuts-title" style={modalTitle}>
              Borrar todos los atajos semánticos
            </h3>
            <p
              id="delete-all-shortcuts-desc"
              style={{ margin: '0 0 14px', fontSize: 14, color: '#334155', lineHeight: 1.6 }}
            >
              ¿Eliminar <strong>todos</strong> los atajos semánticos del agente <strong>«{deleteAllShortcutsModalAgent.name}»</strong> — modelo:{' '}
              <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 6, fontSize: 12 }}>
                {deleteAllShortcutsModalAgent.model?.trim() || '—'}
              </code>
              ?
            </p>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>
              Se borrarán <strong>{shortcutCountFor(deleteAllShortcutsModalAgent)}</strong> atajo
              {shortcutCountFor(deleteAllShortcutsModalAgent) === 1 ? '' : 's'} (manuales y desde conversaciones). La{' '}
              <strong>guía few-shot no se modifica</strong>.
            </p>
            <p style={{ margin: '0 0 20px', fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>Esta acción no se puede deshacer.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={btnOutline}
                disabled={!!deleteAllShortcutsBusyId}
                onClick={() => {
                  if (!deleteAllShortcutsBusyId) setDeleteAllShortcutsModalAgent(null);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={btnModalDanger}
                disabled={!!deleteAllShortcutsBusyId}
                onClick={() => void confirmDeleteAllShortcuts()}
              >
                {deleteAllShortcutsBusyId ? 'Borrando…' : 'Sí, borrar todos los atajos'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editing ? (
        <div
          style={modalOverlay}
          role="dialog"
          aria-modal
          aria-labelledby="edit-ex-title"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) setEditing(null);
          }}
        >
          <form
            onSubmit={(ev) => void saveEdit(ev)}
            style={modalPanel}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="edit-ex-title" style={modalTitle}>
              Editar aprendizaje (guía)
            </h3>
            <label style={lab}>
              Visitante
              <textarea value={eUser} onChange={(e) => setEUser(e.target.value)} required style={ta} rows={2} disabled={eBusy} />
            </label>
            <label style={lab}>
              Asistente
              <textarea value={eAssist} onChange={(e) => setEAssist(e.target.value)} required style={ta} rows={3} disabled={eBusy} />
            </label>
            <label style={lab}>
              Nota
              <textarea value={eNote} onChange={(e) => setENote(e.target.value)} style={ta} rows={2} disabled={eBusy} />
            </label>
            <label style={lab}>
              Contexto de aplicación
              <textarea value={eContext} onChange={(e) => setEContext(e.target.value)} style={ta} rows={2} disabled={eBusy} />
            </label>
            <label style={{ ...lab, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} disabled={eBusy} />
              Activo (máx. {data?.maxActivePerAgent ?? 32} activos por agente)
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button type="submit" style={btnPri} disabled={eBusy}>
                {eBusy ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" style={btnOutline} onClick={() => setEditing(null)} disabled={eBusy}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {autoTrainFor
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="learnings-autotrain-title"
              style={modalOverlay}
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget && !autoTrainBusy) setAutoTrainFor(null);
              }}
            >
              <div
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  ...autoTrainDialogPanel,
                  maxHeight: 'min(92vh, 900px)',
                  overflow: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 10,
                  }}
                >
                  <h2 id="learnings-autotrain-title" style={{ fontSize: 16, margin: 0, fontWeight: 700, color: '#0f172a' }}>
                    Borrador con LLM — {autoTrainFor.name}
                  </h2>
                  <button
                    type="button"
                    aria-label="Cerrar"
                    disabled={autoTrainBusy}
                    onClick={() => {
                      if (!autoTrainBusy) setAutoTrainFor(null);
                    }}
                    style={{
                      border: 'none',
                      background: 'rgba(148,163,184,0.25)',
                      color: '#475569',
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      cursor: autoTrainBusy ? 'not-allowed' : 'pointer',
                      fontSize: 20,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                  El modelo recibe el <strong>mismo armado de system</strong> que en <strong>Simular visitante</strong> (prompt del agente, guía de ejemplos activos, documentos y reglas de chat), más la tarea de generar pares. <strong>Guardar</strong> añade esos pares como <strong>atajos semánticos</strong> (no a la guía). No hay hilo de conversación; revisá y editá antes de guardar.
                </p>
                {autoTrainPhase !== 'idle' ? (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      marginBottom: 16,
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                      border: '1px solid #ddd6fe',
                    }}
                  >
                    <style>
                      {`@keyframes vaLearnPulse {
                          0%, 100% { opacity: 1; transform: scale(1); }
                          50% { opacity: 0.6; transform: scale(0.9); }
                        }`}
                    </style>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#312e81',
                        margin: '0 0 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#7c3aed',
                            animation: autoTrainProgress < 100 ? 'vaLearnPulse 1s ease-out infinite' : 'none',
                            flexShrink: 0,
                          }}
                        />
                        {autoTrainProgress >= 100
                          ? autoTrainPhase === 'propose'
                            ? 'Borrador listo'
                            : 'Guardado'
                          : autoTrainPhase === 'propose'
                            ? `Generando pares: ${autoTrainPairDone} / ${autoTrainN} (objetivo)…`
                            : 'Guardando en atajos…'}
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          fontVariantNumeric: 'tabular-nums',
                          color: '#7c3aed',
                          letterSpacing: '0.02em',
                        }}
                        aria-label={`Progreso ${Math.round(autoTrainProgress)} por ciento`}
                      >
                        {Math.round(autoTrainProgress)}%
                      </span>
                    </p>
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(autoTrainProgress)}
                      aria-busy={autoTrainProgress < 100}
                      aria-label={autoTrainPhase === 'propose' ? 'Generando preguntas' : 'Guardando atajos semánticos'}
                      style={{
                        height: 10,
                        background: 'rgba(255,255,255,0.75)',
                        borderRadius: 999,
                        overflow: 'hidden',
                        border: '1px solid #c4b5fd',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(100, autoTrainProgress)}%`,
                          borderRadius: 999,
                          background: 'linear-gradient(90deg, #7c3aed, #7c3aed, #a78bfa)',
                          transition: 'width 0.18s ease-out',
                        }}
                      />
                    </div>
                    <p style={{ fontSize: 11, color: '#5b21b6', margin: '8px 0 0', lineHeight: 1.45, opacity: 0.95 }}>
                      {autoTrainProgress >= 100
                        ? 'Listo.'
                        : autoTrainPhase === 'propose'
                          ? 'La barra sube con cada par que el modelo completa (una línea = un par en la respuesta). Si el modelo devuelve un solo bloque JSON al final, la barra recién avanza al terminar; podés bajar la cantidad de pares o reintentar.'
                          : 'Creando atajos en la base…'}
                    </p>
                  </div>
                ) : null}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Cant. de pares
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={autoTrainN}
                      onChange={(e) => setAutoTrainN(Number(e.target.value) || 30)}
                      disabled={autoTrainBusy}
                      style={autoTrainNInput}
                    />
                    <span style={{ color: '#94a3b8' }}>(1–200, def. 30)</span>
                  </label>
                  <label
                    style={{
                      fontSize: 12,
                      color: '#334155',
                      display: 'flex',
                      flexDirection: 'column' as const,
                      gap: 4,
                      minWidth: 0,
                    }}
                  >
                    <span>Modelo del borrador</span>
                    <select
                      value={draftLlmModelId}
                      onChange={(e) => setDraftLlmModelId(e.target.value)}
                      disabled={autoTrainBusy}
                      style={{
                        ...autoTrainNInput,
                        minWidth: 220,
                        maxWidth: 'min(100%, 22rem)',
                        padding: '6px 8px',
                        borderRadius: 8,
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                      }}
                    >
                      <option value="">
                        Mismo que el agente{draftTargetAgent ? ` (${draftTargetAgent.model})` : ''}
                      </option>
                      {aiModelPresets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label} — {p.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" style={btnPri} disabled={autoTrainBusy} onClick={() => void runAutoTrainPropose()}>
                    {autoTrainPhase === 'propose' ? 'Generando…' : 'Generar borrador (LLM)'}
                  </button>
                </div>
                {autoTrainMeta ? (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 8px' }}>
                      Modelo:{' '}
                      <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 6 }}>{autoTrainMeta.model}</code>{' '}
                      · destino: {autoTrainMeta.targetName}
                    </p>
                    {autoTrainMeta.usage ? (
                      <div
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 10,
                          padding: '10px 12px',
                          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 6, letterSpacing: '0.02em' }}>
                          Consumo de esta solicitud
                        </div>
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: 18,
                            fontSize: 12,
                            color: '#475569',
                            lineHeight: 1.5,
                          }}
                        >
                          <li>Tokens de entrada: {formatDraftTokens(autoTrainMeta.usage.promptTokens)}</li>
                          <li>Tokens de salida: {formatDraftTokens(autoTrainMeta.usage.completionTokens)}</li>
                          <li>Total: {formatDraftTokens(autoTrainMeta.usage.totalTokens)}</li>
                          <li style={{ marginTop: 4 }}>
                            Coste estimado: <strong style={{ color: '#0f172a' }}>{formatDraftUsd(autoTrainMeta.cost?.estimatedUsd ?? null)}</strong>
                            {autoTrainMeta.cost?.estimatedUsd == null ? (
                              <span style={{ color: '#b45309', fontWeight: 500 }}> (sin tarifa para este modelId en /super/ai; revisá el catálogo)</span>
                            ) : null}
                          </li>
                          {autoTrainMeta.cost &&
                          (autoTrainMeta.cost.inputUsdPerMillion != null || autoTrainMeta.cost.outputUsdPerMillion != null) ? (
                            <li style={{ fontSize: 11, color: '#64748b' }}>
                              Tarifas usadas: entrada US${' '}
                              {autoTrainMeta.cost.inputUsdPerMillion != null
                                ? new Intl.NumberFormat('es-UY', { maximumFractionDigits: 4 }).format(autoTrainMeta.cost.inputUsdPerMillion)
                                : '—'}
                              /1M · salida US${' '}
                              {autoTrainMeta.cost.outputUsdPerMillion != null
                                ? new Intl.NumberFormat('es-UY', { maximumFractionDigits: 4 }).format(autoTrainMeta.cost.outputUsdPerMillion)
                                : '—'}
                              /1M
                            </li>
                          ) : null}
                        </ul>
                        <p style={{ fontSize: 10, color: '#94a3b8', margin: '8px 0 0', lineHeight: 1.4 }}>
                          Misma estimación que en consumo (super): catálogo de modelos o tarifas de referencia. No reemplaza la facturación del proveedor.
                        </p>
                      </div>
                    ) : (
                      <p style={{ fontSize: 11, color: '#b45309', margin: 0, lineHeight: 1.45 }}>
                        No se leyeron tokens de la API en este borrador; el detalle de coste no aplica. Si el problema se repite, revisá claves y logs del servidor.
                      </p>
                    )}
                  </div>
                ) : null}
                {autoTrainItems.length > 0 ? (
                  <div
                    style={{
                      maxHeight: 'min(50vh, 480px)',
                      overflow: 'auto',
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: 16,
                    }}
                  >
                    <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 12px', lineHeight: 1.4 }}>
                      Vista previa al estilo del chat de <strong>Conversaciones</strong>: visitante a la izquierda,
                      respuesta del agente a la derecha con <strong>👍 útil</strong> (solo referencia).
                    </p>
                    {autoTrainItems.map((row, i) => (
                      <div
                        key={i}
                        style={{
                          marginBottom: i < autoTrainItems.length - 1 ? 16 : 0,
                          paddingBottom: i < autoTrainItems.length - 1 ? 16 : 0,
                          borderBottom: i < autoTrainItems.length - 1 ? '1px solid #e2e8f0' : 'none',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em' }}>
                            Par {i + 1} / {autoTrainItems.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => setAutoTrainItems((rows) => rows.filter((_, j) => j !== i))}
                            disabled={autoTrainBusy}
                            style={{ ...btnOutline, fontSize: 12, padding: '4px 10px' }}
                            title="Quitar este par"
                          >
                            Quitar par
                          </button>
                        </div>
                        <div style={{ marginBottom: 10, textAlign: 'left' as const, width: '100%', maxWidth: '100%' }}>
                          <div style={autoTrainVisitorBubbleOuter}>
                            <div style={autoTrainBubbleMetaVisitor}>visitante</div>
                            <textarea
                              style={taInVisitorBub}
                              value={row.userMessage}
                              onChange={(e) => {
                                const v = e.target.value;
                                setAutoTrainItems((rows) => rows.map((r, j) => (j === i ? { ...r, userMessage: v } : r)));
                              }}
                              disabled={autoTrainBusy}
                              rows={3}
                              aria-label="Mensaje del visitante (borrador)"
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row' as const,
                            alignItems: 'flex-start' as const,
                            justifyContent: 'flex-end' as const,
                            gap: 6,
                            width: '100%',
                            maxWidth: '100%',
                          }}
                        >
                          <div style={autoTrainAgentBubbleOuter}>
                            <div style={autoTrainBubbleMetaAgent}>agente · atajo (borrador)</div>
                            <textarea
                              style={taInAgentBub}
                              value={row.assistantMessage}
                              onChange={(e) => {
                                const v = e.target.value;
                                setAutoTrainItems((rows) => rows.map((r, j) => (j === i ? { ...r, assistantMessage: v } : r)));
                              }}
                              disabled={autoTrainBusy}
                              rows={4}
                              aria-label="Respuesta deseada del agente (borrador)"
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column' as const,
                              alignItems: 'center' as const,
                              gap: 3,
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          >
                            <button
                              type="button"
                              disabled
                              tabIndex={-1}
                              style={autoTrainThumbGoodPreview}
                              title="Vista previa: como al marcar «útil» (👍) en el chat de la conversación"
                              aria-label="Vista previa: respuesta útil, como con la valoración en el chat"
                            >
                              👍
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    style={btnOutline}
                    disabled={autoTrainBusy}
                    onClick={() => {
                      if (!autoTrainBusy) setAutoTrainFor(null);
                    }}
                  >
                    Cerrar
                  </button>
                  <button
                    type="button"
                    style={btnPri}
                    disabled={autoTrainBusy || autoTrainItems.length === 0}
                    onClick={() => void runAutoTrainCommit()}
                  >
                    {autoTrainPhase === 'commit' ? 'Guardando…' : 'Guardar en atajos'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const thHead: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  background: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  boxShadow: '0 1px 0 rgba(226, 232, 240, 0.9)',
};
const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'top' as const, color: '#334155', lineHeight: 1.45 };
const tableShell: React.CSSProperties = {
  overflowX: 'auto',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
};

/** Cabecera fija; alto ~8 filas de cuerpo (texto a una línea con clip) + scroll. */
const tableShellScrollable: React.CSSProperties = {
  ...tableShell,
  maxHeight: 'min(28.5rem, 58vh)',
  overflowY: 'auto',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
};
const sectionTitle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#475569',
  margin: '0 0 4px',
};

const sectionCollapseHeader: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  width: '100%',
  margin: 0,
  background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  boxSizing: 'border-box' as const,
  overflow: 'hidden',
  gap: 0,
};

const sectionCollapseToggle: React.CSSProperties = {
  display: 'flex',
  flex: '1 1 auto',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
  margin: 0,
  padding: '10px 12px',
  textAlign: 'left',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
};

const sectionChevron: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
  width: 18,
  display: 'inline-block',
  flexShrink: 0,
};

const sectionTitleInToggle: React.CSSProperties = {
  ...sectionTitle,
  margin: 0,
  flex: '1 1 160px',
  minWidth: 0,
  textAlign: 'left',
};

const sectionHeaderIconGroup: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  alignSelf: 'center',
  gap: 6,
  padding: '4px 10px 4px 4px',
  flexShrink: 0,
  borderLeft: '1px solid #e2e8f0',
  background: 'rgba(255,255,255,0.5)',
};

const headerIconBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: 8,
  border: '1px solid #c4b5fd',
  background: '#fff',
  color: '#7c3aed',
  cursor: 'pointer',
  flexShrink: 0,
  boxSizing: 'border-box' as const,
};

const headerIconBtnActive: React.CSSProperties = {
  ...headerIconBtn,
  background: '#f5f3ff',
  borderColor: '#a78bfa',
  color: '#6d28d9',
};

const headerIconBtnDanger: React.CSSProperties = {
  ...headerIconBtn,
  border: '1px solid #fecaca',
  color: '#b91c1c',
  background: '#fffafa',
  fontSize: 14,
  fontWeight: 800,
  lineHeight: 1,
};

const headerIconBtnShortcut: React.CSSProperties = {
  ...headerIconBtn,
  border: '1px solid #86efac',
  color: '#15803d',
  background: '#f0fdf4',
};

const headerIconBtnShortcutOn: React.CSSProperties = {
  ...headerIconBtnShortcut,
  background: '#dcfce7',
  borderColor: '#4ade80',
  color: '#14532d',
};

const sectionCountPill: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  padding: '3px 10px',
  borderRadius: 999,
  background: '#fff',
  border: '1px solid #e2e8f0',
  marginLeft: 'auto',
};

const emptyWell: React.CSSProperties = {
  textAlign: 'center',
  padding: '24px 16px',
  borderRadius: 12,
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
};
const badgeYes: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  color: '#047857',
  background: '#d1fae5',
};
const badgeNo: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  color: '#64748b',
  background: '#e2e8f0',
};
const codePill: React.CSSProperties = {
  background: '#f1f5f9',
  padding: '4px 10px',
  borderRadius: 8,
  fontSize: 12,
  color: '#0f172a',
  border: '1px solid #e2e8f0',
};
const pillOn: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: 999,
  background: '#ecfdf5',
  color: '#047857',
  border: '1px solid #a7f3d0',
};
const pillOff: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: 999,
  background: '#f1f5f9',
  color: '#64748b',
  border: '1px solid #e2e8f0',
};
const pillStat: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: '#475569',
  padding: '4px 10px',
  borderRadius: 8,
  background: '#fff',
  border: '1px solid #e2e8f0',
};
const lab: React.CSSProperties = { display: 'block', fontSize: 12, color: '#475569', fontWeight: 600, marginBottom: 6 };
const ta: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  marginTop: 4,
  padding: 10,
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  fontFamily: 'inherit',
  lineHeight: 1.45,
  transition: 'border-color 0.15s, box-shadow 0.15s',
  outline: 'none',
};

const autoTrainVisitorBubbleOuter: React.CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: 'min(100%, 42rem)',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '10px 14px',
  borderRadius: 14,
  background: '#e2e8f0',
  color: '#0f172a',
  textAlign: 'left',
};

const autoTrainBubbleMetaVisitor: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  marginBottom: 6,
  opacity: 0.85,
  color: 'rgba(15, 23, 42, 0.6)',
};

const autoTrainAgentBubbleOuter: React.CSSProperties = {
  display: 'block',
  flex: '1 1 auto',
  minWidth: 0,
  width: '100%',
  maxWidth: 'min(100%, 42rem)',
  boxSizing: 'border-box',
  padding: '10px 14px',
  borderRadius: 14,
  background: '#7c3aed',
  color: '#fff',
  textAlign: 'left',
};

const autoTrainBubbleMetaAgent: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  marginBottom: 6,
  opacity: 0.9,
  color: 'rgba(255, 255, 255, 0.82)',
  wordBreak: 'break-word',
};

const taInVisitorBub: React.CSSProperties = {
  display: 'block',
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: '#0f172a',
  fontSize: 14,
  lineHeight: 1.45,
  fontFamily: 'inherit',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  resize: 'vertical',
  minHeight: 40,
  outline: 'none',
  boxSizing: 'border-box',
};

const taInAgentBub: React.CSSProperties = {
  ...taInVisitorBub,
  color: '#fff',
};

const autoTrainThumbGoodPreview: React.CSSProperties = {
  width: 30,
  height: 28,
  padding: 0,
  borderRadius: 8,
  color: '#15803d',
  background: 'rgba(220, 252, 231, 0.95)',
  border: '1px solid #86efac',
  boxShadow: '0 0 0 1px rgba(34, 197, 94, 0.2)',
  fontSize: 15,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  userSelect: 'none',
  cursor: 'default',
  opacity: 1,
};

const selectFilter: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  minWidth: 220,
  maxWidth: '100%',
  background: '#fff',
  color: '#0f172a',
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const btnGhost: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#475569',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const btnPri: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 10,
  border: 'none',
  background: '#7c3aed',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
  boxShadow: '0 1px 2px rgba(79, 70, 229, 0.35)',
};

const modalOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 100,
};

const modalPanel: CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 520,
  width: '100%',
  maxHeight: 'min(90vh, 720px)',
  overflow: 'auto',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.28)',
  border: '1px solid #e2e8f0',
};

const autoTrainDialogPanel: CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 900,
  width: '100%',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.28)',
  border: '1px solid #e2e8f0',
};

const autoTrainNInput: CSSProperties = {
  width: 64,
  margin: 0,
  padding: '6px 8px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  fontFamily: 'inherit',
};

const modalTitle: CSSProperties = {
  marginTop: 0,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid #f1f5f9',
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
};

const btnOutline: React.CSSProperties = {
  padding: '9px 14px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const btnModalDanger: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: '1px solid #b91c1c',
  background: '#b91c1c',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
  boxShadow: '0 1px 2px rgba(185, 28, 28, 0.2)',
};

const btnPrimarySoft: React.CSSProperties = {
  ...btnOutline,
  background: '#f5f3ff',
  borderColor: '#c4b5fd',
  color: '#6d28d9',
};

const btnSubtle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};

const btnLink: React.CSSProperties = {
  ...btnOutline,
  textDecoration: 'none',
  display: 'inline-block',
  lineHeight: 1.4,
};

const btnSm: React.CSSProperties = {
  padding: '6px 10px',
  marginRight: 6,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#334155',
  cursor: 'pointer',
};
